const KIND_LABELS = {
  infill: 'Infill',
  seam_tape: 'Seam Tape',
  glue: 'Glue',
};

class GGCartMaterialRecommendations extends HTMLElement {
  connectedCallback() {
    if (!window.GoGreenMaterialsCalculator) {
      document.addEventListener('gg:materials-calculator:ready', () => this.connectedCallback(), { once: true });
      return;
    }

    this.attachListeners();
    this.refreshRecommendations();
  }

  disconnectedCallback() {
    this.detachListeners();
    if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
  }

  attachListeners() {
    if (this.listenersAttached) return;
    this.addEventListener('click', this.handleClick);
    document.addEventListener('cart:update', this.handleCartUpdate);
    this.listenersAttached = true;
  }

  detachListeners() {
    if (!this.listenersAttached) return;
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener('cart:update', this.handleCartUpdate);
    this.listenersAttached = false;
  }

  scheduleRefresh() {
    if (this.refreshFrame) cancelAnimationFrame(this.refreshFrame);
    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = null;
      this.refreshRecommendations();
    });
  }

  refreshRecommendations() {
    this.refreshElements();
    this.recommendations = this.getRecommendations();
    this.renderDebug();

    if (!this.recommendations.length) {
      this.panel?.setAttribute('hidden', '');
      this.itemsContainer?.replaceChildren();
      return;
    }

    this.renderRecommendations();
    this.panel?.removeAttribute('hidden');
  }

  refreshElements() {
    this.panel = this.querySelector('[data-gg-cart-materials-panel]');
    this.itemsContainer = this.querySelector('[data-gg-cart-materials-items]');
    this.addButton = this.querySelector('[data-gg-cart-materials-add]');
    this.dismissButton = this.querySelector('[data-gg-cart-materials-dismiss]');
    this.status = this.querySelector('[data-gg-cart-materials-status]');
  }

  parseNumber(value) {
    const number = Number.parseFloat(String(value || '').replace(/,/g, ''));
    return Number.isFinite(number) ? number : 0;
  }

  getRecommendations() {
    const configuration = this.querySelector('[data-gg-cart-materials-config]');
    if (configuration) {
      try {
        const input = JSON.parse(configuration.textContent || '{}');
        return window.GoGreenMaterialsCalculator.calculateCart(input).recommendations.map((recommendation) => ({
          variantId: recommendation.variantId,
          sku: recommendation.sku,
          quantity: recommendation.quantity,
          title: recommendation.label,
          kind: recommendation.kind,
          price: recommendation.price,
          linePrice: recommendation.linePrice,
          available: recommendation.available,
          warning: recommendation.warning,
          sources: ['Cart turf cuts'],
        }));
      } catch (error) {
        console.error('[Go Green materials] Unable to calculate cart recommendations.', error);
      }
    }

    return [];
  }

  renderRecommendations() {
    if (!this.itemsContainer) return;
    this.itemsContainer.replaceChildren();

    for (const recommendation of this.recommendations) {
      const item = document.createElement('div');
      item.className = 'gg-cart-materials__item';

      const label = document.createElement('span');
      const kind = KIND_LABELS[recommendation.kind] || 'Material';
      label.textContent = `${kind}: ${recommendation.title}`;
      if (recommendation.available === false) label.textContent += ' (Unavailable)';

      const quantity = document.createElement('span');
      quantity.textContent = `Qty ${recommendation.quantity}`;

      item.append(label, quantity);
      this.itemsContainer.append(item);
    }
  }

  renderDebug() {
    // Retained as a no-op for compatibility with existing call sites.
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  dispatchCartError(message, description, errors) {
    this.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          sourceId: this.id,
          data: {
            message,
            description,
            errors,
          },
        },
      })
    );
  }

  dispatchCartAdd(cart, itemCount, sections) {
    this.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: this.id,
          data: {
            source: 'gg-cart-material-recommendations',
            itemCount,
            sections,
          },
        },
      })
    );
  }

  handleDismiss = () => {
    this.panel?.setAttribute('hidden', '');
  };

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-gg-cart-materials-add]')) {
      this.handleAdd();
      return;
    }

    if (target.closest('[data-gg-cart-materials-dismiss]')) {
      this.refreshElements();
      this.handleDismiss();
    }
  };

  handleCartUpdate = () => {
    this.scheduleRefresh();
  };

  handleAdd = async () => {
    if (this.isAdding) return;
    this.isAdding = true;

    this.refreshElements();
    this.recommendations = this.getRecommendations();

    const items = this.recommendations.map((recommendation) => ({
      recommendation,
    }))
      .filter(({ recommendation }) => recommendation.available !== false)
      .map(({ recommendation }) => ({
        id: Number(recommendation.variantId),
        quantity: recommendation.quantity,
        properties: {
          _source: 'Cart recommended installation materials',
        },
      }));

    if (!items.length) {
      this.setStatus('Recommended materials are currently unavailable.');
      this.isAdding = false;
      return;
    }
    if (this.addButton instanceof HTMLButtonElement) this.addButton.disabled = true;
    this.setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.description || result.message || 'Unable to add materials to cart.';
        this.dispatchCartError(message, result.description, result.errors);
        this.setStatus(message);
        return;
      }

      const cart = await fetch(Theme.routes.cart_url + '.js').then((cartResponse) => cartResponse.json());
      this.dispatchCartAdd(cart, items.reduce((sum, item) => sum + item.quantity, 0), result.sections);
      this.recommendations = [];
      this.panel?.setAttribute('hidden', '');
      this.itemsContainer?.replaceChildren();
      this.renderDebug();
    } catch (error) {
      console.error(error);
      this.setStatus('Unable to add materials to cart.');
    } finally {
      if (this.addButton instanceof HTMLButtonElement) this.addButton.disabled = false;
      this.isAdding = false;
    }
  };
}

if (!customElements.get('gg-cart-material-recommendations')) {
  customElements.define('gg-cart-material-recommendations', GGCartMaterialRecommendations);
}
